import {Injectable, inject} from '@angular/core';
import {AuthService} from './auth.service';
import {consoleOrigin} from '../console-url';

/** What the console hands /impersonate (its ImpersonationService). */
export interface ImpersonationHandOff {
  type: 'mm-impersonate';
  /** The customer's sign-in token (a Firebase custom token carrying impersonatedBy). */
  targetToken: string;
  targetUid: string;
  targetName: string;
  adminName: string;
}

/** What this tab remembers while an admin is viewing someone else's account. */
export interface ImpersonationSession {
  adminName: string;
  targetUid: string;
  targetName: string;
  startedAt: number;
}

const STORAGE_KEY = 'mm.impersonation';

/**
 * "View as user", started in the admin console.
 *
 * The console opens /impersonate in a new tab and hands it the customer's
 * sign-in token by postMessage (never in a URL). This tab signs in as the
 * customer; the bar at the top says so, and "Return" signs the customer out
 * and closes the tab, back to the console, whose own session was never
 * touched. The marker is in sessionStorage: this tab only, gone when it
 * closes.
 */
@Injectable({providedIn: 'root'})
export class ImpersonationService {
  private auth = inject(AuthService);

  get session(): ImpersonationSession | null {
    let session: ImpersonationSession | null = null;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      session = raw ? JSON.parse(raw) as ImpersonationSession : null;
    } catch (e) {
      return null;
    }
    if (!session) {
      return null;
    }
    // A marker left behind by a manual sign-out (or another account in the
    // same tab) would otherwise offer a "return" that goes nowhere.
    const currentUid = this.auth.currentUserInfo?.uid;
    if (currentUid && currentUid !== session.targetUid) {
      this.clear();
      return null;
    }
    return session;
  }

  get isImpersonating(): boolean {
    return !!this.session;
  }

  /** Signs this tab in as the customer the console named. */
  async startFromConsole(handOff: ImpersonationHandOff): Promise<void> {
    await this.auth.signOut();
    await this.auth.loginWithCustomToken(handOff.targetToken);
    const session: ImpersonationSession = {
      adminName: handOff.adminName,
      targetUid: handOff.targetUid,
      targetName: handOff.targetName,
      startedAt: Date.now(),
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    window.location.href = '/dashboard/overview';
  }

  /** Back to the console: the customer signed out, this tab closed. */
  async stop(): Promise<void> {
    const session = this.session;
    this.clear();
    await this.auth.signOut();
    window.close();
    // Not every browser lets a page close itself: then to the account in the console.
    window.location.href = `${consoleOrigin()}/users/${encodeURIComponent(session?.targetUid ?? '')}`;
  }

  /** Drop the marker without switching accounts (e.g. after a manual logout). */
  clear() {
    sessionStorage.removeItem(STORAGE_KEY);
  }
}
